import { useContext, useEffect, useState } from 'react';
import AvailabilityAndPriceContext from '~/context/home/AvailabilityAndPriceContext';
import MegaEventContext from '~/context/home/MegaEventContext';
import type {
  BulkAvailabilityPayload,
  RecommendedVehicle
} from '~/contracts/common';
import type { CarouselVehicles } from '~/contracts/home';
import useGetBecauseYouViewed from './useGetBecauseYouViewed';
import useGetBudgetFriendlyVehicles from './useGetBudgetFriendlyVehicle';
import useGetFreeShippingVehicles from './useGetFreeShippingVehicles';
import useGetPriceDropVehicles from './useGetPriceDropVehicles';
import useGetUpgradeYourRideVehicles from './useGetUpgradeYourRideVehicles';

export const defaultCarouselVehicles: CarouselVehicles = {
  becauseYouViewed: [],
  freeShipping: [],
  upYourRide: [],
  budgetFriendly: [],
  priceDrops: []
};

export default function useGetCarouselVehicles() {
  const {
    carouselVehicleAvailability: { setData: setAvailabilityData }
  } = useContext(AvailabilityAndPriceContext);
  const { setCarouselVehicles: setMegaEventCarouselVehicles } =
    useContext(MegaEventContext);
  const [carouselVehicles, setCarouselVehicles] = useState(
    defaultCarouselVehicles
  );

  const { isLoaded: becauseYouViewedLoaded, vehicles: becauseYouViewed } =
    useGetBecauseYouViewed();
  const {
    isLoaded: freeShippingVehiclesLoaded,
    vehicles: freeShippingVehicles
  } = useGetFreeShippingVehicles();
  const {
    isLoaded: upgradeYourRideVehiclesLoaded,
    vehicles: upgradeYourRideVehicles
  } = useGetUpgradeYourRideVehicles();
  const {
    isLoaded: budgetFriendlyVehiclesLoaded,
    vehicles: budgetFriendlyVehicles
  } = useGetBudgetFriendlyVehicles();
  const { isLoaded: priceDropVehiclesLoaded, vehicles: priceDropVehicles } =
    useGetPriceDropVehicles();

  const allLoaded =
    becauseYouViewedLoaded &&
    freeShippingVehiclesLoaded &&
    upgradeYourRideVehiclesLoaded &&
    budgetFriendlyVehiclesLoaded &&
    priceDropVehiclesLoaded;

  useEffect(() => {
    if (!allLoaded) return;
    const rawVehicles = [
      becauseYouViewed,
      freeShippingVehicles,
      upgradeYourRideVehicles,
      budgetFriendlyVehicles,
      priceDropVehicles
    ];

    let filteredValues: RecommendedVehicle[][] = [];
    let totalIds: number[] = [];

    rawVehicles.forEach((vehicleSet, index) => {
      const filteredSet = vehicleSet.filter(
        vehicle => !totalIds.includes(vehicle.vehicleId)
      );

      const filteredIds = filteredSet.map(vehicle => vehicle.vehicleId);

      filteredValues[index] = filteredSet;
      totalIds.push(...filteredIds);
    });

    const becauseYouViewedSet =
      filteredValues[0].length < 6 ? [] : filteredValues[0];
    const freeShippingSet =
      filteredValues[1].length < 6 ? [] : filteredValues[1];
    const upgradeYourRideSet =
      filteredValues[2].length < 6 ? [] : filteredValues[2];
    const budgetFriendlySet =
      filteredValues[3].length < 6 ? [] : filteredValues[3];
    const priceDropsSet = filteredValues[4].length < 6 ? [] : filteredValues[4];

    const carouselVehiclesSet: CarouselVehicles = {
      becauseYouViewed: becauseYouViewedSet,
      freeShipping: freeShippingSet,
      upYourRide: upgradeYourRideSet,
      budgetFriendly: budgetFriendlySet,
      priceDrops: priceDropsSet
    };

    setCarouselVehicles(carouselVehiclesSet);
    setMegaEventCarouselVehicles(carouselVehiclesSet);

    const availabilityData: BulkAvailabilityPayload[] = [
      ...becauseYouViewedSet,
      ...freeShippingSet,
      ...upgradeYourRideSet,
      ...budgetFriendlySet,
      ...priceDropsSet
    ].map(({ vehicleId, locationId, vehicleInventoryType }) => ({
      vehicleId,
      vehicleLocationId: locationId,
      vehicleInventoryType
    }));
    setAvailabilityData(availabilityData);
  }, [
    becauseYouViewed,
    freeShippingVehicles,
    upgradeYourRideVehicles,
    budgetFriendlyVehicles,
    priceDropVehicles,
    allLoaded,
    setAvailabilityData,
    setMegaEventCarouselVehicles
  ]);

  return { carouselVehicles, showCarousel: allLoaded };
}
